import React, { useState, useEffect } from 'react';
import Sidebar from '../components/sidebar';
import { 
  Table, 
  Button, 
  Modal, 
  Input, 
  message, 
  Space, 
  Tag, 
  Card, 
  Typography, 
  Row, 
  Col,
  Statistic,
  Select
} from 'antd';
import { 
  EyeOutlined, 
  CheckOutlined, 
  CloseOutlined, 
  MessageOutlined,
  EditOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import axios from 'axios';

const { TextArea } = Input;
const { Title, Text } = Typography;
const { Option } = Select;

const GestionarComentarios = () => {
  const [comentarios, setComentarios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [estadisticas, setEstadisticas] = useState({});
  const [selectedComentario, setSelectedComentario] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showRespuestaModal, setShowRespuestaModal] = useState(false);
  const [respuesta, setRespuesta] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('todos');
  const [usuario, setUsuario] = useState({});

  useEffect(() => {
    const loginUserJSON = window.localStorage.getItem("loginUser");
    if (loginUserJSON) {
      const user = JSON.parse(loginUserJSON);
      setUsuario(user);
    }
  }, []);

  // Cargar comentarios
  const cargarComentarios = async () => {
    setLoading(true);
    try {
      const response = await axios.get('https://api.mrcirp.com/public/comentarios-modulos');
      setComentarios(response.data);
    } catch (error) {
      console.error('Error al cargar comentarios:', error);
      message.error('Error al cargar los comentarios');
    } finally {
      setLoading(false);
    }
  };

  // Cargar estadísticas
  const cargarEstadisticas = async () => {
    try {
      const response = await axios.get('https://api.mrcirp.com/public/comentarios-modulos/estadisticas');
      setEstadisticas(response.data);
    } catch (error) {
      console.error('Error al cargar estadísticas:', error);
    }
  };

  useEffect(() => {
    cargarComentarios();
    cargarEstadisticas();
  }, []);

  // Formatear fecha
  const formatearFecha = (fecha) => {
    return new Date(fecha).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Moderar comentario
  const moderarComentario = async (id, aprobado) => {
    try {
      await axios.put(`https://api.mrcirp.com/public/comentarios-modulos/${id}/moderar`, {
        moderado: aprobado ? 1 : 0,
        admin_id: usuario.id,
        admin_nombre: usuario.nombre || usuario.username
      });

      message.success(aprobado ? 'Comentario aprobado' : 'Comentario rechazado');
      cargarComentarios();
      cargarEstadisticas();
    } catch (error) {
      console.error('Error al moderar comentario:', error);
      message.error('Error al moderar el comentario');
    }
  };

  // Responder a comentario
  const responderComentario = async () => {
    if (!respuesta.trim()) {
      message.warning('Por favor escribe una respuesta');
      return;
    }

    try {
      await axios.put(`https://api.mrcirp.com/public/comentarios-modulos/${selectedComentario.id}/responder`, {
        respuesta_admin: respuesta.trim(),
        admin_id: usuario.id,
        admin_nombre: usuario.nombre || usuario.username
      });

      message.success('Respuesta enviada exitosamente');
      setShowRespuestaModal(false);
      setRespuesta('');
      setSelectedComentario(null);
      cargarComentarios();
    } catch (error) {
      console.error('Error al responder comentario:', error);
      message.error('Error al enviar la respuesta');
    }
  };

  // Eliminar comentario
  const eliminarComentario = async (id) => {
    try {
      await axios.delete(`https://api.mrcirp.com/public/comentarios-modulos/${id}`);
      message.success('Comentario eliminado');
      cargarComentarios();
      cargarEstadisticas();
    } catch (error) {
      console.error('Error al eliminar comentario:', error);
      message.error('Error al eliminar el comentario');
    }
  };

  // Filtrar comentarios
  const comentariosFiltrados = comentarios.filter(comentario => {
    if (filtroEstado === 'todos') return true;
    if (filtroEstado === 'pendientes') return !comentario.moderado;
    if (filtroEstado === 'aprobados') return comentario.moderado && comentario.activo;
    if (filtroEstado === 'rechazados') return comentario.moderado && !comentario.activo;
    return true;
  });

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
    },
    {
      title: 'Módulo',
      dataIndex: 'modulo_nombre',
      key: 'modulo_nombre',
      width: 150,
      ellipsis: true,
    },
    {
      title: 'Usuario',
      dataIndex: 'usuario_nombre',
      key: 'usuario_nombre',
      width: 120,
    },
    {
      title: 'Comentario',
      dataIndex: 'comentario',
      key: 'comentario',
      width: 200,
      ellipsis: true,
      render: (text) => (
        <div style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {text}
        </div>
      )
    },
    {
      title: 'Estado',
      key: 'estado',
      width: 120,
      render: (_, record) => {
        if (!record.moderado) {
          return <Tag color="orange">Pendiente</Tag>;
        } else if (record.activo) {
          return <Tag color="green">Aprobado</Tag>;
        } else {
          return <Tag color="red">Rechazado</Tag>;
        }
      }
    },
    {
      title: 'Fecha',
      dataIndex: 'fecha_creacion',
      key: 'fecha_creacion',
      width: 140,
      render: (fecha) => formatearFecha(fecha),
    },
    {
      title: 'Acciones',
      key: 'acciones',
      width: 200,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="primary"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => {
              setSelectedComentario(record);
              setShowModal(true);
            }}
            title="Ver detalles"
          />
          
          {!record.moderado && (
            <>
              <Button
                type="primary"
                size="small"
                icon={<CheckOutlined />}
                onClick={() => moderarComentario(record.id, true)}
                title="Aprobar"
                style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
              />
              <Button
                type="primary"
                size="small"
                icon={<CloseOutlined />}
                onClick={() => moderarComentario(record.id, false)}
                title="Rechazar"
                danger
              />
            </>
          )}
          
          <Button
            type="default"
            size="small"
            icon={<MessageOutlined />}
            onClick={() => {
              setSelectedComentario(record);
              setShowRespuestaModal(true);
            }}
            title="Responder"
          />
          
          <Button
            type="primary"
            danger
            size="small"
            icon={<DeleteOutlined />}
            onClick={() => eliminarComentario(record.id)}
            title="Eliminar"
          />
        </Space>
      )
    }
  ];

  return (
    <Sidebar>
      <div style={{ padding: 24 }}>
        <Title level={2}>Gestión de Comentarios</Title>
        
        {/* Estadísticas */}
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={6}>
            <Card>
              <Statistic
                title="Total Comentarios"
                value={estadisticas.total_comentarios || 0}
                prefix={<MessageOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Pendientes"
                value={estadisticas.comentarios_pendientes || 0}
                valueStyle={{ color: '#faad14' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Aprobados"
                value={estadisticas.comentarios_aprobados || 0}
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Respondidos"
                value={estadisticas.comentarios_respondidos || 0}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
        </Row>

        {/* Filtros */}
        <Card style={{ marginBottom: 16 }}>
          <Space>
            <Text strong>Filtrar por estado:</Text>
            <Select
              value={filtroEstado}
              onChange={setFiltroEstado}
              style={{ width: 150 }}
            >
              <Option value="todos">Todos</Option>
              <Option value="pendientes">Pendientes</Option>
              <Option value="aprobados">Aprobados</Option>
              <Option value="rechazados">Rechazados</Option>
            </Select>
            <Button onClick={cargarComentarios}>Actualizar</Button>
          </Space>
        </Card>

        {/* Tabla de comentarios */}
        <Card>
          <Table
            columns={columns}
            dataSource={comentariosFiltrados}
            loading={loading}
            rowKey="id"
            scroll={{ x: 1000 }}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => 
                `${range[0]}-${range[1]} de ${total} comentarios`
            }}
          />
        </Card>

        {/* Modal para ver detalles */}
        <Modal
          title="Detalles del Comentario"
          open={showModal}
          onCancel={() => setShowModal(false)}
          footer={[
            <Button key="close" onClick={() => setShowModal(false)}>
              Cerrar
            </Button>
          ]}
          width={600}
        >
          {selectedComentario && (
            <div>
              <div style={{ marginBottom: 16 }}>
                <Text strong>Módulo:</Text> {selectedComentario.modulo_nombre}
              </div>
              <div style={{ marginBottom: 16 }}>
                <Text strong>Usuario:</Text> {selectedComentario.usuario_nombre}
              </div>
              <div style={{ marginBottom: 16 }}>
                <Text strong>Email:</Text> {selectedComentario.usuario_email}
              </div>
              <div style={{ marginBottom: 16 }}>
                <Text strong>Fecha:</Text> {formatearFecha(selectedComentario.fecha_creacion)}
              </div>
              <div style={{ marginBottom: 16 }}>
                <Text strong>Comentario:</Text>
                <div style={{ 
                  marginTop: 8, 
                  padding: 12, 
                  background: '#f5f5f5', 
                  borderRadius: 6 
                }}>
                  {selectedComentario.comentario}
                </div>
              </div>
              
              {selectedComentario.respuesta_admin && (
                <div>
                  <Text strong>Respuesta del administrador:</Text>
                  <div style={{ 
                    marginTop: 8, 
                    padding: 12, 
                    background: '#f0f8ff', 
                    borderRadius: 6,
                    borderLeft: '3px solid #1890ff'
                  }}>
                    {selectedComentario.respuesta_admin}
                  </div>
                  {selectedComentario.fecha_respuesta && (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      - {selectedComentario.admin_nombre} • {formatearFecha(selectedComentario.fecha_respuesta)}
                    </Text>
                  )}
                </div>
              )}
            </div>
          )}
        </Modal>

        {/* Modal para responder */}
        <Modal
          title="Responder al Comentario"
          open={showRespuestaModal}
          onCancel={() => {
            setShowRespuestaModal(false);
            setRespuesta('');
            setSelectedComentario(null);
          }}
          onOk={responderComentario}
          okText="Enviar Respuesta"
          cancelText="Cancelar"
          width={600}
        >
          {selectedComentario && (
            <div>
              <div style={{ marginBottom: 16 }}>
                <Text strong>Comentario de {selectedComentario.usuario_nombre}:</Text>
                <div style={{ 
                  marginTop: 8, 
                  padding: 12, 
                  background: '#f5f5f5', 
                  borderRadius: 6 
                }}>
                  {selectedComentario.comentario}
                </div>
              </div>
              
              <div>
                <Text strong>Tu respuesta:</Text>
                <TextArea
                  rows={4}
                  placeholder="Escribe tu respuesta aquí..."
                  value={respuesta}
                  onChange={(e) => setRespuesta(e.target.value)}
                  style={{ marginTop: 8 }}
                />
              </div>
            </div>
          )}
        </Modal>
      </div>
    </Sidebar>
  );
};

export default GestionarComentarios;

